import React, { useState, useMemo } from 'react';
import { FileText, Download, Loader2, BookOpen, Target, AlertCircle, CheckSquare, Maximize2, Minimize2, ExternalLink } from 'lucide-react';
import { useSyllabus } from '../../hooks/useStudent';
import { getFullFileUrl, getDownloadFileUrl } from '@/utils/fileUrl';

interface SyllabusTabProps {
  moduleElementId: string;
  // Past year = read-only consultation; absent = current year
  academicYearId?: string;
  // Course instance (the student's group, or a former one)
  groupeId?: string;
}

// Helper to get file extension
const getFileExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

// Helper to determine file type category
const getFileTypeCategory = (fileName: string): 'pdf' | 'office' | 'image' | 'unknown' => {
  const ext = getFileExtension(fileName);
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(ext)) return 'office';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext)) return 'image';
  return 'unknown';
};

// Document Viewer Component
const DocumentViewer: React.FC<{ fileUrl: string; fileName: string }> = ({ fileUrl, fileName }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fileType = useMemo(() => getFileTypeCategory(fileName), [fileName]);

  // For Office documents, use Microsoft Office Online viewer
  const viewerUrl = useMemo(() => {
    if (fileType === 'office') {
      // Microsoft Office Online viewer - works with public URLs
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;
    }
    // For PDFs, use Google Docs viewer as fallback (better cross-browser support)
    if (fileType === 'pdf') {
      // Try direct embed first, Google Docs as fallback
      return fileUrl;
    }
    return fileUrl;
  }, [fileUrl, fileType]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Render based on file type
  const renderViewer = () => {
    if (fileType === 'image') {
      return (
        <div className="flex items-center justify-center bg-gray-100 rounded-lg p-4">
          <img
            src={fileUrl}
            alt={fileName}
            className="max-w-full max-h-[500px] object-contain rounded"
            onLoad={handleLoad}
            onError={handleError}
          />
        </div>
      );
    }

    if (fileType === 'pdf') {
      return (
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          )}
          <iframe
            src={`${fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
            className={`w-full rounded-lg border border-gray-200 ${isFullscreen ? 'h-[80vh]' : 'h-[500px]'}`}
            title={fileName}
            onLoad={handleLoad}
            onError={handleError}
          />
          {hasError && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                Impossible d'afficher le PDF directement.
                <a
                  href={`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  Ouvrir avec Google Docs Viewer <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>
          )}
        </div>
      );
    }

    if (fileType === 'office') {
      return (
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 rounded-lg">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          )}
          <iframe
            src={viewerUrl}
            className={`w-full rounded-lg border border-gray-200 ${isFullscreen ? 'h-[80vh]' : 'h-[500px]'}`}
            style={{ border: 'none' }}
            title={fileName}
            onLoad={handleLoad}
            onError={handleError}
          />
          {hasError && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                Impossible d'afficher le document directement.
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1 text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  Ouvrir dans un nouvel onglet <ExternalLink className="h-3 w-3" />
                </a>
              </p>
            </div>
          )}
        </div>
      );
    }

    // Unknown file type - show download prompt
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <FileText className="h-16 w-16 text-gray-400 mb-4" />
        <p className="text-gray-600 mb-2">Aperçu non disponible pour ce type de fichier</p>
        <p className="text-sm text-gray-500">Téléchargez le fichier pour le consulter</p>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* Viewer Controls */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500">
          {getFileExtension(fileName).toUpperCase()} • Aperçu du document
        </span>
        {(fileType === 'pdf' || fileType === 'office') && (
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isFullscreen ? (
              <>
                <Minimize2 className="h-4 w-4" />
                Réduire
              </>
            ) : (
              <>
                <Maximize2 className="h-4 w-4" />
                Agrandir
              </>
            )}
          </button>
        )}
      </div>

      {/* Document Viewer */}
      {renderViewer()}
    </div>
  );
};

const SyllabusTab: React.FC<SyllabusTabProps> = ({ moduleElementId, academicYearId, groupeId }) => {
  const { data: syllabus, isLoading, error } = useSyllabus(moduleElementId, academicYearId, groupeId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        Erreur lors du chargement du syllabus
      </div>
    );
  }

  if (!syllabus) {
    return (
      <div className="text-center py-12">
        <FileText className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Syllabus non disponible</h3>
        <p className="mt-2 text-sm text-gray-500">
          Le professeur n'a pas encore publié le syllabus pour ce cours.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <FileText className="h-5 w-5 text-blue-600" />
        Syllabus du cours
      </h2>

      {/* Document Viewer with Download */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
        {/* Header with file info and download */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 rounded-lg">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{syllabus.fileName}</h3>
              <p className="text-sm text-gray-500">Syllabus du cours</p>
            </div>
          </div>
          <a
            href={getDownloadFileUrl(syllabus.fileUrl)}
            download={syllabus.fileName}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            Télécharger
          </a>
        </div>

        {/* Embedded Document Viewer */}
        <DocumentViewer fileUrl={getFullFileUrl(syllabus.fileUrl)} fileName={syllabus.fileName} />
      </div>

      {/* Description */}
      {syllabus.description && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-gray-600" />
            Description du cours
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">{syllabus.description}</p>
        </div>
      )}

      {/* Objectives */}
      {syllabus.objectives && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Target className="h-5 w-5 text-green-600" />
            Objectifs pédagogiques
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">{syllabus.objectives}</p>
        </div>
      )}

      {/* Prerequisites */}
      {syllabus.prerequisites && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            Prérequis
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">{syllabus.prerequisites}</p>
        </div>
      )}

      {/* Evaluation Criteria */}
      {syllabus.evaluationCriteria && (
        <div className="bg-white rounded-lg border border-gray-200 p-5">
          <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-purple-600" />
            Critères d'évaluation
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">{syllabus.evaluationCriteria}</p>
        </div>
      )}
    </div>
  );
};

export default SyllabusTab;
